// JS is enabled
document.querySelector("html").classList.add("js");

// Immediate fix for back button issue - ensure page is visible on load
(function () {
  // Remove loading state immediately to prevent empty page on back navigation
  const body = document.body;
  if (body && body.classList.contains("page-transition")) {
    body.classList.remove("is-loading");
  }
})();

// Global page transition: soft dissolve on load and navigation
(function setupPageTransitions() {
  const body = document.body;
  if (!body || !body.classList.contains("page-transition")) return;

  const prefersReduced = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  const fadeDurationMs = 250;

  function fadeInOnLoad() {
    // Always ensure loading class is removed on page load
    body.classList.remove("is-loading");
    // Allow CSS to apply initial state, then remove loading
    requestAnimationFrame(() => {
      body.classList.remove("is-loading");
    });
  }

  function handleLinkNavigation(event) {
    const anchor = event.currentTarget;
    const url = anchor.getAttribute("href");
    const target = anchor.getAttribute("target");
    if (!url || url.startsWith("#") || (target && target === "_blank")) return;

    // Ignore external links
    const isExternal =
      anchor.origin && anchor.origin !== window.location.origin;
    if (isExternal) return;

    event.preventDefault();
    if (!prefersReduced) body.classList.add("is-loading");
    setTimeout(
      () => {
        window.location.href = url;
      },
      prefersReduced ? 0 : fadeDurationMs
    );
  }

  function wireLinkFades() {
    const anchors = Array.from(document.querySelectorAll("a[href]"));
    anchors.forEach((a) => {
      // Skip if already wired
      if (a.dataset.fadeWired === "true") return;
      a.addEventListener("click", handleLinkNavigation);
      a.dataset.fadeWired = "true";
    });
  }

  function wireFormFades() {
    const forms = Array.from(document.querySelectorAll("form"));
    forms.forEach((form) => {
      if (form.dataset.fadeWired === "true") return;
      form.addEventListener("submit", () => {
        if (!prefersReduced) body.classList.add("is-loading");
      });
      form.dataset.fadeWired = "true";
    });
  }

  // Handle page visibility changes (back/forward navigation)
  function handleVisibilityChange() {
    if (!document.hidden) {
      // Page became visible (user navigated back/forward)
      fadeInOnLoad();
    }
  }

  // Handle browser back/forward navigation
  function handlePopState() {
    fadeInOnLoad();
  }

  // Initial
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", fadeInOnLoad);
  } else {
    fadeInOnLoad();
  }

  // Wire global handlers
  document.addEventListener("DOMContentLoaded", () => {
    wireLinkFades();
    wireFormFades();
  });

  // Handle page visibility and navigation events
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("popstate", handlePopState);
  window.addEventListener("pageshow", fadeInOnLoad);
})();

/**
 * Modals with the native <dialog> element
 *
 * Allows to load content from an external URL when opened,
 * which works well with the Shopify Section Rendering API.
 */
class DialogOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const dialog = document.querySelector(this.getAttribute("data-dialog"));
      if (dialog) dialog.show(button);
    });
  }
}

customElements.define("dialog-opener", DialogOpener);

class DialogModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.dialog = this.querySelector("dialog");
    this.content = this.querySelector(".dialog-content");
    this.load = this.getAttribute("data-load");
    this.loaded = false;
    this.loadingClass = "loading";

    this.dialog.addEventListener("click", this.handleClose.bind(this));
  }

  show() {
    this.dialog.showModal();
    this.loadContent();
  }

  // Close when clicked outside the dialog.
  // <form method="dialog"> used for close button.
  handleClose(event) {
    const x = event.clientX;
    const y = event.clientY;
    const dialog = this.dialog.getBoundingClientRect();

    // X and Y are 0 if keyboard navigation is used,
    // which can result in unwanted closing of modal.
    // The close button will still work with <form>.
    if (x === 0 && y === 0) return;

    if (
      x < dialog.left ||
      x > dialog.right ||
      y < dialog.top ||
      y > dialog.bottom
    ) {
      this.dialog.close();
    }
  }

  // Load content from URL if data-load attribute is set and not yet loaded
  loadContent() {
    if (this.content && this.load && !this.loaded) {
      this.content.classList.add(this.loadingClass);
      fetch(this.load)
        .then((response) => {
          if (!response.ok) throw new Error(response.statusText);
          return response.text();
        })
        .then((text) => {
          this.content.innerHTML = text;
          this.loaded = true;
        })
        .catch((error) => {
          this.content.innerHTML = "Failed to load content";
          console.error(error);
        })
        .finally(() => {
          this.content.classList.remove(this.loadingClass);
        });
    }
  }
}

customElements.define("dialog-modal", DialogModal);

/**
 * StickyHeader
 * Shows the header when scrolling up
 */
class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector(".section-header");
    this.height = this.header.offsetHeight;
    this.currentScrollTop = 0;
    this.stuckClass = "sticky-header-stuck";
    this.hiddenClass = "sticky-header-hidden";
    window.addEventListener("scroll", this.onScroll.bind(this));
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScroll.bind(this));
  }

  onScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    if (scrollTop === 0) {
      window.requestAnimationFrame(this.reset.bind(this));
    }

    if (scrollTop < this.height) return;

    if (scrollTop < this.currentScrollTop) {
      window.requestAnimationFrame(this.show.bind(this));
    } else {
      window.requestAnimationFrame(this.hide.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  reset() {
    this.header.classList.remove(this.stuckClass);
    this.header.classList.remove(this.hiddenClass);
  }

  hide() {
    this.header.classList.add(this.hiddenClass);
    this.header.classList.remove(this.stuckClass);
  }

  show() {
    this.header.classList.remove(this.hiddenClass);
    this.header.classList.add(this.stuckClass);
  }
}

customElements.define("sticky-header", StickyHeader);
